'use client';

import debounce from 'lodash/debounce';
import { useCallback, useEffect } from 'react';

import type { FC} from 'react';

/**
 * Adds support for usage of the `.breakout` css class.
 * Namely it registers a calculated `--compass-breakout-scrollbar-width` property.
 *
 * - Make sure to add `overflow-y-scroll` to your body class name.
 * - Add this component as the **first child** of your **body element**.
 *
 * Usage:
 *
 * ```tsx
 * return (
 *   <body>
 *    <BreakoutSupport />
 *    { myOtherChildrenOfBody }
 *   </body>
 * );
 * ```
 */
export const BreakoutSupport: FC = () => {
  const handleUpdate = useCallback(() => {
    const width = window.innerWidth - document.documentElement.offsetWidth;

    document.documentElement.style.setProperty(
      '--compass-breakout-scrollbar-width',
      `${width}px`,
    );

    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    const hasClassName = document.body.className.includes('overflow-y-scroll');

    if (!hasClassName) {
      throw new Error(
        "To get full `.breakout` support you need to add `overflow-y-scroll` to your `body` tag's class name.",
      );
    }
  }, []);

   
  const handleUpdateDebounced = useCallback(debounce(handleUpdate, 200), [
    handleUpdate,
  ]);

  useEffect(() => {
    window.addEventListener('resize', handleUpdateDebounced);
    window.addEventListener('load', handleUpdate);

    return () => {
      window.removeEventListener('resize', handleUpdateDebounced);
      window.removeEventListener('load', handleUpdate);
    };
  }, [handleUpdate, handleUpdateDebounced]);

  return (
    <script
      dangerouslySetInnerHTML={{
        __html:
          "document.documentElement.style.setProperty('--compass-breakout-scrollbar-width', (window.innerWidth - document.documentElement.offsetWidth) + 'px');",
      }}
    />
  );
};
